/**
 * One player, end to end: identity, balances, the money roll-up, and their
 * deposits, withdrawals and rounds. Action buttons allow banning or deleting
 * a player.
 */

import { useEffect, useState } from 'react';
import { api } from '../api/client.ts';
import { at, etb } from '../format.ts';
import { ConfirmButton, ErrorBanner, Field, Row, Spinner, Status } from '../components/ui.tsx';
import type { AdminPlayerDetailView, AdminView, PlayerStatus } from '../api/types.ts';
import { can } from '../auth.ts';

export function PlayerDetail({ id, me }: { id: string; me: AdminView }) {
  const [player, setPlayer] = useState<AdminPlayerDetailView | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const [acting, setActing] = useState(false);
  const [rate, setRate] = useState('');

  useEffect(() => {
    void api
      .player(id)
      .then(setPlayer)
      .catch(setError);
  }, [id]);

  const changeStatus = async (status: PlayerStatus) => {
    setActing(true);
    setActionError(null);
    try {
      await api.setPlayerStatus(id, status);
      setPlayer((prev) => (prev ? { ...prev, status } : prev));
    } catch (err) {
      setActionError(err);
    } finally {
      setActing(false);
    }
  };

  /**
   * Makes this player an agent, or changes their cut (070). Entered as a
   * percentage because that is how the deal is agreed with a person; stored
   * as basis points because that is how commission is computed everywhere
   * else. Blank clears it — they stop earning on future rounds and keep
   * everything already accrued.
   */
  const saveAgentRate = async () => {
    const trimmed = rate.trim();
    const pct = trimmed === '' ? null : Number(trimmed);
    if (pct !== null && (!Number.isFinite(pct) || pct < 0 || pct > 100)) {
      setActionError(new Error('Rate must be a percentage between 0 and 100.'));
      return;
    }
    setActing(true);
    setActionError(null);
    try {
      const bps = pct === null ? null : Math.round(pct * 100);
      await api.setAgentRate(id, bps);
      setPlayer((prev) => (prev ? { ...prev, agentCommissionBps: bps } : prev));
      setRate('');
    } catch (err) {
      setActionError(err);
    } finally {
      setActing(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('This will close the player account permanently. Continue?')) return;
    setActing(true);
    setActionError(null);
    try {
      await api.deletePlayer(id);
      setPlayer((prev) => (prev ? { ...prev, status: 'closed' } : prev));
    } catch (err) {
      setActionError(err);
    } finally {
      setActing(false);
    }
  };

  return (
    <section>
      <ErrorBanner error={error} />
      {!player && !error ? <Spinner /> : null}

      {player ? (
        <>
          <header className="pagehead">
            <h1>{player.fullName ?? player.username ?? player.referralCode}</h1>
            <Status value={player.status} />
          </header>

          {can('superadmin', 'user.manage') || can('finance', 'user.manage') || can('operator', 'user.manage') ? (
            <div className="card" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {player.status === 'active' ? (
                <button
                  type="button"
                  className="button"
                  disabled={acting}
                  onClick={() => changeStatus('suspended')}
                >
                  Ban player
                </button>
              ) : player.status === 'suspended' ? (
                <>
                  <button
                    type="button"
                    className="button"
                    disabled={acting}
                    onClick={() => changeStatus('active')}
                  >
                    Unban
                  </button>
                  <button
                    type="button"
                    className="button"
                    style={{ color: 'var(--tg-destructive)' }}
                    disabled={acting}
                    onClick={handleDelete}
                  >
                    Delete
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="button"
                  disabled={acting}
                  onClick={() => changeStatus('active')}
                >
                  Reactivate
                </button>
              )}
              <ErrorBanner error={actionError} />
            </div>
          ) : null}

          {can(me.role, 'agent.manage') ? (
            <div className="card">
              <h2>Agent</h2>
              <p className="muted">
                An agent earns this share of the house commission the players they referred
                generate. Earnings accrue per round and are paid by hand from the Agents screen —
                setting a rate here moves no money.
              </p>
              <Row label="Current rate">
                {player.agentCommissionBps === null
                  ? 'Not an agent'
                  : `${String(player.agentCommissionBps / 100)}%`}
              </Row>
              <Field label="New rate (%)" hint="Leave blank and save to stop them being an agent.">
                <input
                  className="input"
                  inputMode="decimal"
                  placeholder={player.agentCommissionBps === null ? 'e.g. 10' : ''}
                  value={rate}
                  onChange={(e) => setRate(e.target.value)}
                />
              </Field>
              <ConfirmButton
                className="button button--primary"
                confirm={
                  rate.trim() === ''
                    ? 'Stop this player being an agent? They keep anything already earned.'
                    : `Give this player ${rate.trim()}% of the commission their referrals generate?`
                }
                disabled={acting}
                onConfirm={() => void saveAgentRate()}
              >
                Save
              </ConfirmButton>
            </div>
          ) : null}

          <div className="card">
            <h2>Account</h2>
            <Row label="Telegram id">{player.telegramId}</Row>
            <Row label="Username">{player.username ? `@${player.username}` : '—'}</Row>
            <Row label="Referral code">
              <code>{player.referralCode}</code>
            </Row>
            <Row label="Referred players">{player.referredCount}</Row>
            <Row label="Joined">{at(player.createdAt)}</Row>
            <Row label="Available balance">{etb(player.available)}</Row>
            <Row label="Locked in play">{etb(player.locked)}</Row>
          </div>

          <div className="card">
            <h2>Lifetime totals</h2>
            <Row label="Deposited (approved)">{etb(player.totalDeposited)}</Row>
            <Row label="Withdrawn (completed)">{etb(player.totalWithdrawn)}</Row>
            <Row label="Winnings">{etb(player.totalWinnings)}</Row>
            <Row label="Wins">{player.wins}</Row>
            <Row label="Rounds played">{player.roundsPlayed}</Row>
          </div>

          <Activity player={player} />
        </>
      ) : null}
    </section>
  );
}

function Activity({ player }: { player: AdminPlayerDetailView }) {
  return (
    <>
      <div className="card">
        <h2>Deposits</h2>
        {player.deposits.length === 0 ? (
          <p className="muted">No deposits.</p>
        ) : (
          <table className="table" role="table">
            <thead role="rowgroup">
              <tr role="row">
                <th scope="col" role="columnheader">Date</th>
                <th scope="col" role="columnheader">Amount</th>
                <th scope="col" role="columnheader">Method</th>
                <th scope="col" role="columnheader">Reference</th>
                <th scope="col" role="columnheader">Status</th>
              </tr>
            </thead>
            <tbody role="rowgroup">
              {player.deposits.map((d) => (
                <tr role="row" key={d.id}>
                  <td role="cell" data-label="Date" className="muted">{at(d.createdAt)}</td>
                  <td role="cell" data-label="Amount">{etb(d.amount)}</td>
                  <td role="cell" data-label="Method">{d.method}</td>
                  <td role="cell" data-label="Reference">
                    <code>{d.txReference}</code>
                  </td>
                  <td role="cell" data-label="Status">
                    <Status value={d.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card">
        <h2>Withdrawals</h2>
        {player.withdrawals.length === 0 ? (
          <p className="muted">No withdrawals.</p>
        ) : (
          <table className="table" role="table">
            <thead role="rowgroup">
              <tr role="row">
                <th scope="col" role="columnheader">Date</th>
                <th scope="col" role="columnheader">Amount</th>
                <th scope="col" role="columnheader">Method</th>
                <th scope="col" role="columnheader">Destination</th>
                <th scope="col" role="columnheader">Status</th>
              </tr>
            </thead>
            <tbody role="rowgroup">
              {player.withdrawals.map((w) => (
                <tr role="row" key={w.id}>
                  <td role="cell" data-label="Date" className="muted">{at(w.createdAt)}</td>
                  <td role="cell" data-label="Amount">{etb(w.amount)}</td>
                  <td role="cell" data-label="Method">{w.method}</td>
                  <td role="cell" data-label="Destination" className="muted">{w.destinationDetail}</td>
                  <td role="cell" data-label="Status">
                    <Status value={w.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card">
        <h2>Rounds played</h2>
        {player.rounds.length === 0 ? (
          <p className="muted">No rounds yet.</p>
        ) : (
          <table className="table" role="table">
            <thead role="rowgroup">
              <tr role="row">
                <th scope="col" role="columnheader">Ended</th>
                <th scope="col" role="columnheader">Game</th>
                <th scope="col" role="columnheader">My cartelas</th>
                <th scope="col" role="columnheader">Winner</th>
                <th scope="col" role="columnheader">Won?</th>
              </tr>
            </thead>
            <tbody role="rowgroup">
              {player.rounds.map((r) => (
                <tr role="row" key={r.roundId}>
                  <td role="cell" data-label="Ended" className="muted">{at(r.endedAt)}</td>
                  <td role="cell" data-label="Game">{r.gameTypeName ?? '—'}</td>
                  <td role="cell" data-label="My cartelas">
                    <code>{r.myCartelaNumbers.join(', ') || '—'}</code>
                  </td>
                  <td role="cell" data-label="Winner">
                    {r.winners.length === 0
                      ? '—'
                      : r.winners.map((w) => `${w.firstName} (#${w.cartelaNumber})`).join(', ')}
                  </td>
                  <td role="cell" data-label="Won?">
                    <Status value={r.status === 'cancelled' ? 'cancelled' : r.won ? 'won' : 'lost'} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
